import React, { useContext, useMemo, useState, Suspense } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  VStack,
  Text,
  HStack,
  Box,
  Img,
  Heading,
  Progress,
  Button,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useMutation } from '@apollo/client';
import styled from '../../../../consts/styled';
import { spacing } from '../../../../App/ChakraTheme';
import { Tabs } from '../../../../components/Tabs';

import { axiosWrapper } from '../../../../hooks/axios';
import ClassComponent from '../../components/ClassComponent';

import { UnitPage } from '../Unit/UnitPage';
import GuidesPage from '../Guides';
import { AuthContext, Role } from '../../../../context/AuthProvider';
import { PreparationMaterialComponent } from '../../components/PreparationMaterial';

import { NeverShrink, StyledTabs } from './components';
import StudentTeams from './StudentTeams/StudentTeams';
import useTutorialInfo, { InfoStep } from '../../../../hooks/useTutorialInfo';
import DotInfo from '../../../../components/DotInfo';
import DiplomaInputComponent from '../../components/DiplomaInputComponent';
import { fullNameUsernameOrEmail } from '../../../../helpers/username';

import { useQueryParam } from '../../../../hooks/useQueryParams';
import useGetUnitDataGql from '../Unit/hooks/useGetUnitDataGql';
import ChakraReactSelect, {
  OptionType,
} from '../Unit/UnitPageComponents/ChakraReactSelect';
import {
  Mutation,
  StudyGroup,
  TrackUnit,
  Unit,
} from '../../../../generated/graphqlModels';
import { CHOOSE_TRACK, QUERY_CAN_BE_SHARED } from '../Unit/queries';
import TrackAssignments from './TrackAssignments';
import { trackAction } from '../../../../helpers/trackAction';
import { Link } from 'react-router-dom';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import { StemiBeatLoader } from '../../../../components/Loader';
const LazyAnalyticsSubPage = React.lazy(() => import('./Analytics'));

const RASA_PK = '99596423-b56d-4261-ba97-b08c8e28b98e';

// const diploma_programs = [
//   'a34cfae4-f1d9-4d92-91f7-7f581d1aaed6', // Izrada AI Chatbota 2.0
//   'b0da5aeb-0067-4e84-8d19-792cd3a94abc', // Robotika i Automatizirani sustavi
//   '63fa0978-7122-4ebf-b8d1-7fe5db3a7e43', // Greentech IoT
//   'f74b5e5c-c72b-4ed3-9670-3d90e293af70', // AI 3.0
//   '349e64ac-6765-4f08-9cd4-226397b00ffd', // Equinox
// ];
// const diploma_namespaces = ['hr', 'bih'];

const diploma_programs_new = [
  'b0da5aeb-0067-4e84-8d19-792cd3a94abc', // Robotika i automatizirani sustavi
  '349e64ac-6765-4f08-9cd4-226397b00ffd', // Gen Future
  '5cd43ea5-624d-4942-aa44-3d509b6106a7', // From AI Concepts to Creations
  '95531f73-9524-4418-aed5-1d3ca14dcbd6', // Bit Game Development
];

const diploma_namespaces_new = ['hr'];

interface Props {
  className?: string;
}

const Wrapper = styled(VStack)`
  padding-left: ${spacing.BASE_PLUS};
  padding-right: ${spacing.BASE_PLUS};
  //padding-bottom: ${spacing.LARGE};

  .class-image {
    width: 754px;
    height: 425px;
    border-radius: 4px;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    video {
      width: 100%;
    }
  }

  .tabs-content-centered {
    max-width: 900px;
    margin: 0 auto;

    &.analytics {
      width: 100%;
      max-width: 1300px;
      .insights {
        width: 100%;
        max-width: 1300px;
      }
    }
    &.conversations {
      max-width: 1300px;
    }

    &.center {
      text-align: center;
    }
  }
`;

//TODO we show Answers tab only based on programs name for now, should change this in future
const programNamesSubstringsToDisplayAnswerTabs = [
  'chatb',
  'AI',
  'Dos conceitos de IA',
]

const ClassPage = (props: Props) => {
  const { id, activeTab } = useParams<{ id: string; activeTab: string }>();
  const qStudyGroupId = useQueryParam('study-group-id');
  // const qTrackId = useQueryParam('track-id');
  const edit = useQueryParam('edit');
  const challenge = useQueryParam('challenge');
  const publicProgram = useQueryParam('public');
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { t } = useTranslation();
  const programId = id;

  const isEdit = edit === 'true';
  const isChallenge = challenge === 'true';
  const isPublicProgram = publicProgram === 'true';

  const user = useContext(AuthContext);
  const [tabActiveIndex, setTabActiveIndex] = useState(0);

  const [unitsLoading, setUnitsLoading] = useState<string[]>([]);

  const [state, updateState, { loading }] = useGetUnitDataGql({
    programId,
    contentLevel: 'program',
    props,
    params,
    context: user,
    isEdit,
    isChallenge,
  });

  const [refectCanBeShared] = useLazyQuery(QUERY_CAN_BE_SHARED);

  const [chooseTrack] = useMutation<Mutation>(CHOOSE_TRACK);

  const trackOptions = useMemo(() => {
    const opts: OptionType[] = [];
    state.tracks?.forEach((t) => {
      opts.push({ id: t.id, label: t.name });
    });
    return opts;
  }, [state.tracks]);

  const share = (unit: Unit, trackUnit: TrackUnit, studyGroup: StudyGroup) => ({
    ...unit,
    disabled: unitsLoading.includes(unit.id),
    onShare: () => {
      if (unitsLoading.includes(unit.id)) {
        return;
      }

      if (window.confirm('Are you sure?')) {
        setUnitsLoading((u) => [...u, unit.id]);
        trackAction({
          action_type: 'button_click',
          action_object: { pk: unit.id, type: 'unit' },
          study_group: { pk: studyGroup.pk },
          data: { text: 'Share All' },
        });
        axiosWrapper(
          `/api/v1/records/shares/unit/${unit.id}/${state.studyGroupId}/`,
          'post',
          {},
        )
          .then(() =>
            refectCanBeShared({
              variables: { trackUnitId: trackUnit.id },
              context: { studyGroupId: studyGroup.id },
            }),
          )
          .then(() => setUnitsLoading((u) => u.filter((i) => i !== unit.id)))
          .catch((err: any) => console.error(err));
      }
    },
  });

  const isTeacherOrStaff = (user?.roles || []).some((role) =>
    ['teacher', 'staff', 'organization_user'].includes(role),
  );
  const isTeacherOrManagerOrStaff =
    isTeacherOrStaff || user?.roles.includes(Role.manager);

  const { currentStep, nextStep } = useTutorialInfo();
  const showInfo = currentStep === InfoStep.OPEN_UNIT;
  const units = isEdit
    ? state?.program?.programUnits
      ? [
          ...(state?.program?.programUnits.map((u) => ({
            unit: u,
            canBeShared: -1,
            progress: 100,
          })) as any as TrackUnit[]),
          {
            id: '-1',
            conditionEnabled: true,
            conditionShown: true,
            enabled: true,
            canBeShared: false,
            progress: 100,
            unit: {
              id: '-1',
              isDemo: false,
              name: 'Add new unit',
            },
          } as any as TrackUnit,
        ]
      : []
    : state?.track?.trackUnits?.filter((tu) => tu.conditionShown);

  const getTabContent: {
    name: string | JSX.Element;
    slug: string;
    content?: JSX.Element;
  }[] = [
    {
      name: t('class.units'),
      slug: 'units',
      content: (
        <VStack mt="-8" w="full">
          {state.tracks && state.tracks.length > 1 && (
            <VStack>
              <Text>
                {state.trackId
                  ? t('class.selectedLearningPath')
                  : t('class.selectLearningPath')}
              </Text>
              <ChakraReactSelect
                selectedId={state.trackId}
                options={trackOptions}
                onSelect={(trackId) => {
                  if (state.studyGroupId) {
                    chooseTrack({
                      variables: {
                        trackId,
                        studyGroupId: state.studyGroupId,
                      },
                    });
                    trackAction({
                      action_type: 'choose_track',
                      study_group: { pk: state.studyGroupId },
                      data: { trackId },
                    });
                  }
                  updateState({ trackId });
                }}
              />
            </VStack>
          )}
          {(state.isTrackLoading || loading) && <StemiBeatLoader />}
          <HStack
            align="stretch"
            pt="8"
            w="full"
            h="full"
            mx="-8px"
            flexWrap="wrap"
            alignContent="stretch"
            spacing="0px"
            style={{
              justifyContent:
                state.isTrackLoading || loading ? 'center' : 'flex-start',
            }}
          >
            {units?.map((trackUnit, index) => {
              if (
                !isChallenge && !isPublicProgram &&
                user?.roles.length === 1 &&
                user?.roles[0] === Role.student &&
                trackUnit.canBeShared === -1
              ) {

                return null;
              }
              const isDemoUserAndUnitIsNot =
                user?.is_demo && !trackUnit.unit.isDemo;
              const isInfoShown =
                showInfo &&
                state?.track?.trackUnits
                  ?.filter((tu) => tu.conditionShown)
                  ?.findIndex((tu) => tu.unit.isDemo) === index;
              const isCreateNew = trackUnit.id === '-1';
              const editLink = isCreateNew
                ? '/admin/syllabus/unit/add/'
                : `/admin/syllabus/unit/${trackUnit.unit.id}/change/`;
              const toLink = isCreateNew
                ? editLink
                : UnitPage.unit.getRoute(
                    programId,
                    trackUnit.unit.id,
                    location.search,
                  );
              return (
                <VStack
                  px="2"
                  py="2"
                  w={state.program?.newMenu ? '25%' : '33.3%'}
                  flexBasis={[
                    '100%',
                    '100%',
                    '50%',
                    '33.3%',
                    state.program?.newMenu ? '25%' : '33.3%',
                  ]}
                  key={trackUnit.unit.id}
                  style={{ boxSizing: 'border-box', position: 'relative' }}
                  onClick={() => {
                    nextStep();
                  }}
                >
                  {isInfoShown ? (
                    <DotInfo
                      info="Open unit"
                      style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                      }}
                    />
                  ) : null}
                  <ClassComponent
                    new={state.program?.newMenu || false}
                    to={toLink}
                    programId={programId}
                    classId={state.studyGroupId}
                    isUnit
                    target={isCreateNew ? '_blank' : '_self'}
                    isStaff={(user?.roles || []).includes(Role.staff)}
                    isTeacherOrStaff={isTeacherOrStaff}
                    {...trackUnit.unit}
                    canBeShared={trackUnit.canBeShared}
                    progress={trackUnit.progress}
                    isSelfLearning={
                      state.studyGroup?.isSelfLearning || isChallenge
                    }
                    isChallenge={isChallenge}
                    isPublic={isPublicProgram}
                    edit={isEdit}
                    editLink={editLink}
                    share={
                      !isChallenge &&
                      (user?.roles.includes(Role.organization_user) ||
                        user?.roles.includes(Role.teacher) ||
                        user?.roles.includes(Role.manager) ||
                        user?.roles.includes(Role.staff))
                        ? {
                            canBeShared: trackUnit.canBeShared || 0,
                            ...share(
                              trackUnit.unit,
                              trackUnit,
                              // @ts-expect-error
                              state.studyGroup,
                            ),
                            text:
                              trackUnit.canBeShared !== 1
                                ? unitsLoading.includes(trackUnit.unit.id) ||
                                  trackUnit.canBeShared == null
                                  ? t('class.loading')
                                  : trackUnit.canBeShared === 0
                                  ? t('class.nothingIsShared')
                                  : trackUnit.canBeShared === -1
                                  ? t('class.nothingCanBeShared')
                                  : t('class.only', {
                                      percentage:
                                        Math.round(
                                          (trackUnit.canBeShared || 0) * 10000,
                                        ) / 100,
                                    })
                                : t('class.alreadyFullyShared'),
                          }
                        : undefined
                    }
                    enabled={isDemoUserAndUnitIsNot ? false : user?.pages.unit}
                  />
                </VStack>
              );
            })}
          </HStack>
        </VStack>
      ),
    },
  ];

  const guides =
    state?.track?.trackGuides?.map((tg) => tg.guide) ||
    state?.program?.guides ||
    [];
  if (guides && isTeacherOrManagerOrStaff) {
    getTabContent.push({
      name: t('class.preparationMaterials'),
      slug: 'preparation-materials',
      content: loading ? (
        <StemiBeatLoader />
      ) : guides.length === 0 ? (
        <Text
          as="div"
          textStyle="h5"
          className={'tabs-content-centered'}
          style={{ textAlign: 'center' }}
        >
          <p>{t('class.noPreparationMaterials')}</p>
        </Text>
      ) : (
        <VStack
          w="full"
          className={'tabs-content-centered'}
          px={2}
          style={{ maxWidth: 800, margin: '0 auto' }}
          spacing={0}
          gap={4}
        >
          {[...guides]?.sort((a, b) => a.sortOrder - b.sortOrder)?.map((guide) => (
            <PreparationMaterialComponent
              key={guide.id}
              obj={guide}
              route={GuidesPage.getRoute(
                state.program?.id,
                guide.id,
                location.search,
              )}
              user={user}
            />
          ))}
        </VStack>
      ),
    });
  }
  
  const isRasa = state.program?.pk === RASA_PK;
  const isChatbot = programNamesSubstringsToDisplayAnswerTabs.some(substr =>
    !!state.program?.name.includes(substr))
  const isChatbot3_0 = isChatbot && !!state.program?.name.includes('3.0');
  const isPlatformio = !!state.program?.name.includes('Roboti');

  const currentMonth = new Date().getMonth() + 1;

  const isDiplomaTime =
    (user?.namespaces &&
      user.namespaces.filter((n) => diploma_namespaces_new.includes(n)).length >
        0 &&
      diploma_programs_new.includes(state.program?.pk || '') &&
      (new Date(state.studyGroup?.license.period.lastDate) <= new Date() ||
        (currentMonth >= 6 && currentMonth <= 8) ||
        false)) ||
    user?.flags.includes('diploma-master');

  const openTab = (index: number) => {
    const tab = getTabContent[index];
    if (!tab) {
      return;
    }
    trackAction({
      action_type: 'open_tab',
      study_group: { pk: state?.studyGroup?.pk },
      data: { text: tab.name, url: location.pathname },
    });
    if (!tab.content) {
      navigate(tab.slug);
      return;
    }
    const tabName = tab.slug;
    if (activeTab !== tabName) {
      navigate(`/program/${id}/${tabName}/${location.search}`, {
        replace: true,
      });
    }
    setTabActiveIndex(index);
  };

  if (isChatbot) {
    const infobipHref = qStudyGroupId
      ? `https://lab.stemi.education/idp/infobip/start?study-group-id=${qStudyGroupId}`
      : 'https://lab.stemi.education/idp/infobip/start';
    getTabContent.push({
      name: (
        <a href={infobipHref} target="_blank" rel="noreferrer">
          Answers
          <ExternalLinkIcon ml="1" mt="-1" />
        </a>
      ),
      slug: 'answers',
      content: (
        <a href={infobipHref} target="_blank" rel="noreferrer">
          {t('class.answersInfobip')}
          <ExternalLinkIcon ml="1" mt="-1" />
        </a>
      ),
    });
    const hasTutor = state.track?.name.includes('tutor');
    if (hasTutor) {
      const tutorUrl = `/program/${state.program?.id}/content/02e16437-cf1c-485d-8e4d-5da1288356e1${window.location.search}`;
      getTabContent.push({
        name: (
          <a href={tutorUrl} rel="noreferrer">
            {t('class.tutor')}
          </a>
        ),
        slug: 'tutor',
        content: (
          <a href={tutorUrl} rel="noreferrer">
            {t('class.openTutor')}
          </a>
        ),
      });
    }
  }

  if (
    state.studyGroup &&
    !state.studyGroup?.isSelfLearning &&
    isTeacherOrStaff &&
    !user?.is_demo
  ) {
    getTabContent.push({
      name: t('class.studentTeams'),
      slug: 'student-teams',
      content: (
        <HStack style={{ justifyContent: 'center' }}>
          {state.program && state.studyGroup && (
            <StudentTeams
              studyGroupPk={state.studyGroup.id}
              students={state.studyGroup.students}
              program={state.program}
            />
          )}
        </HStack>
      ),
    });
  }

  if (
    state.studyGroup &&
    !state.studyGroup?.isSelfLearning &&
    isTeacherOrStaff
  ) {
    getTabContent.push({
      name: t('class.analytics'),
      slug: 'analytics',
      content: (
        <div className="tabs-content-centered analytics center">
          {state.program && state.studyGroup && (
            <Suspense fallback={<div>Loading...</div>}>
              <LazyAnalyticsSubPage
                program={state.program}
                studyGroup={state.studyGroup}
                isProgramUsingFileServer={isChatbot || isPlatformio}
                isProgramUsingGPT={isChatbot3_0}
                namespace={isPlatformio ? 'PLATFORMIO' : 'CHATBOT'}
              />
            </Suspense>
          )}
        </div>
      ),
    });
  }

  if (
    state.studyGroup &&
    !state.studyGroup?.isSelfLearning &&
    isRasa &&
    isTeacherOrStaff
  ) {
    getTabContent.push({
      name: t('class.teacherDashboard'),
      slug: 'teacher-dashboard',
      content: (
        <div className={'tabs-content-centered center'}>
          <Button as={Link} to={`/editor/dashboard/${state?.studyGroup?.id}`}>
            {t('class.openTeacherDashboard')}
          </Button>
        </div>
      ),
    });
  }

  if (
    state.studyGroup &&
    !state.studyGroup?.isSelfLearning &&
    isTeacherOrManagerOrStaff &&
    state.tracks &&
    state.tracks.length > 1
  ) {
    getTabContent.push({
      name: t('class.learningPaths'),
      slug: 'tracks',
      content: (
        <HStack justify="center" w="full">
          <TrackAssignments
            tracks={state.tracks}
            studyGroup={state.studyGroup}
          />
        </HStack>
      ),
    });
  }

  if (
    state.studyGroup &&
    !state.studyGroup?.isSelfLearning &&
    isDiplomaTime &&
    isTeacherOrManagerOrStaff
  ) {
    getTabContent.push({
      name: t('class.diploma'),
      slug: 'diploma',
      content: (
        <>
          {state.isStudyGroupLoading ? (
            <StemiBeatLoader />
          ) : (
            <>
              {!isTeacherOrManagerOrStaff ? (
                <VStack>
                  <DiplomaInputComponent
                    programId={state.program?.id}
                    initialName={user ? fullNameUsernameOrEmail(user) : ''}
                    studyGroupId={state.studyGroup?.id}
                  />
                </VStack>
              ) : (
                <VStack>
                  {state.studyGroup ? (
                    <>
                      <Text fontWeight="bold" pb={4}>
                        {t('class.students')}
                      </Text>
                      {state.studyGroup.students.map((s) => (
                        <DiplomaInputComponent
                          key={s.pk}
                          programId={state.program?.id}
                          initialName={fullNameUsernameOrEmail(s)}
                          studyGroupId={state.studyGroup?.id}
                        />
                      ))}
                      <Text fontWeight="bold" pt={8} pb={4}>
                        {t('class.teachers')}
                      </Text>
                      {state.studyGroup.teachers.map((t) => (
                        <DiplomaInputComponent
                          key={t.pk}
                          programId={state.program?.id}
                          isTeacher={true}
                          initialName={fullNameUsernameOrEmail(t)}
                          studyGroupId={state.studyGroup?.id}
                        />
                      ))}
                    </>
                  ) : (
                    <StemiBeatLoader />
                  )}
                </VStack>
              )}
            </>
          )}
        </>
      ),
    });
  }

  if (
    state.studyGroup &&
    !state.studyGroup?.isSelfLearning &&
    isTeacherOrManagerOrStaff
  ) {
    if (state.studyGroup?.license?.id) {
      getTabContent.push({
        name: 'Manage',
        slug: `/licenses/${state.studyGroup?.license?.id}`,
      });
    }
  }

  if (activeTab !== ':activeTab') {
    const wantedTabIndex = getTabContent.findIndex(
      (tc) => tc.slug === activeTab,
    );
    if (wantedTabIndex !== -1 && tabActiveIndex !== wantedTabIndex)
      setTabActiveIndex(wantedTabIndex);
  } else {
    const tabName = getTabContent[tabActiveIndex]?.slug;
    if (activeTab !== tabName) {
      navigate(`/program/${id}/${tabName}/${location.search}`, {
        replace: true,
      });
    }
  }

  if (isEdit) {
    const editUrl = `/admin/syllabus/program/${id}/change`;
    getTabContent.push({
      name: (
        <a href={editUrl} target="_blank" rel="noreferrer">
          Edit
          <ExternalLinkIcon ml="1" mt="-1" />
        </a>
      ),
      slug: 'Edit',
      content: (
        <a href={editUrl} target="_blank" rel="noreferrer">
          Edit
          <ExternalLinkIcon ml="1" mt="-1" />
        </a>
      ),
    });
  }

  return (
    <>
      <Box
        minHeight={['300px', '400px', '500px', '600px']}
        width="100%"
        position="relative"
        justifyContent="center"
        alignItems="center"
        display="flex"
        flexDirection="column"
        marginBottom={isChallenge ? '100px' : '0px'}
      >
        {(state.isTrackLoading || state.isStudyGroupLoading) && (
          <StemiBeatLoader />
        )}
        <Heading
          fontSize={['24px', '36px', '48px', '60px']}
          style={{
            color: '#FFF',
            textShadow: '1px 1px 8px #8a8a8a',
            textAlign: 'center',
          }}
        >
          {state.program?.name || 'Class'}
        </Heading>
        <Heading
          fontSize={['18px', '28px', '38px', '48px']}
          style={{
            color: '#FFF',
            textShadow: '1px 1px 8px #8a8a8a',
            textAlign: 'center',
          }}
        >
          {state.studyGroup?.name}
        </Heading>
        {state.program?.videoUrl ? (
          <video
            /* @ts-expect-error */
            type="video/mp4"
            src={state.program?.videoUrl}
            autoPlay={true}
            loop={true}
            preload={'true'}
            muted={true}
            style={{
              width: '100%',
              position: 'absolute',
              top: 0,
              height: '100%',
              objectFit: 'cover',
              zIndex: -1,
            }}
          />
        ) : state.program?.thumbnails?.['754x425'] ? (
          <Img
            src={
              state.program?.image?.name
                ? '/media/' + state.program?.image?.name
                : state.program?.thumbnails?.['754x425']
            }
            alt="program header"
            width="100%"
            height="100%"
            objectFit="cover"
            position="absolute"
            top="0"
            zIndex={-1}
          />
        ) : null}
      </Box>
      {!isChallenge && (
        <>
          <Wrapper className="h-center">
            <Progress
              w="full"
              maxW="500px"
              h="10px"
              mt={8}
              backgroundColor="gray.300"
              value={state.track?.progress || 0}
              borderRadius="30px"
            />
          </Wrapper>
          <StyledTabs>
            <Tabs
              activeIndex={tabActiveIndex}
              tabNames={getTabContent.map((tab) => tab.name)}
              onClick={openTab}
            />
          </StyledTabs>
        </>
      )}
      <NeverShrink>
        <Wrapper>{getTabContent[tabActiveIndex]?.content}</Wrapper>
      </NeverShrink>
    </>
  );
};

ClassPage.getRoute = (id = ':id', activeTab = ':activeTab?', search = '') =>
  `/program/${id}/${activeTab}/${search}`;

export default ClassPage;
